  
import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

export default function MemoiresAdmin() {
  const [rows, setRows] = useState([]);                
  const [regions, setRegions] = useState([]);
  const [regionId, setRegionId] = useState("");       
  const [status, setStatus] = useState("valide");     
  const [q, setQ] = useState("");                     
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [comments, setComments] = useState({});
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedMemoire, setSelectedMemoire] = useState(null);
  const [memoires, setMemoires] = useState([]);       

  const apiBaseUrl = process.env.REACT_APP_API_BASE;

  /* ---------- Chargement filtres régions ---------- */
  useEffect(() => {
    api.get("/region/list")
      .then(res => setRegions(res.data || []))
      .catch(() => setRegions([]));
  }, []);

  const load = async () => {
    try {
      setLoading(true); setErr("");
      const params = new URLSearchParams();
      if (regionId) params.set("region_id", regionId);
      if (status)   params.set("status", status);
      if (q.trim()) params.set("q", q.trim());

      const url = `/adminMemoires/memoires${params.toString() ? `?${params.toString()}` : ""}`;
      const res = await api.get(url);
      setRows(res.data || []);
    } catch (e) {
      console.error("Erreur chargement mémoires (admin):", e?.response || e);
      setErr(e?.response?.data?.error || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [regionId, status]);

  /* ---------- Filtre texte côté client (Admin) ---------- */
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(m =>
      [m.aspirant_name, m.title, m.region_name, m.district_name, m.eglise_name]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(s))
    );
  }, [rows, q]);

  const badge = (st) => st === "valide" ? "success" : st === "refuse" ? "danger" : "warning text-dark";
  const onChangeComment = (id, val) => setComments(prev => ({ ...prev, [id]: val }));

  /* ---------- Modale commentaire (Admin bas) ---------- */
  const openCommentModal = (m) => {
    setSelectedMemoire(m);
    setComments(prev => ({ ...prev, [m.id]: m.comment || "" }));
    setShowCommentModal(true);
  };
  const closeCommentModal = () => { setShowCommentModal(false); setSelectedMemoire(null); };

  /* ---------- MAJ statut depuis tableau Admin (bas) ---------- */
  const updateStatus = async (id, newStatus) => {
    try {
      if (newStatus === "refuse" && !(comments[id]?.trim())) {
        const ok = window.confirm("Aucun commentaire saisi. Continuer quand même ?");
        if (!ok) return;
      }
      await api.patch(`/adminMemoires/memoires/${id}/validate`, {
        status: newStatus,
        comment: comments[id]?.trim() || null,
      });
      setRows(prev => prev.map(r => r.id === id ? {
        ...r,
        status: newStatus,
        validated_at: newStatus === "valide" ? new Date().toISOString() : r.validated_at,
        comment: comments[id]?.trim() || r.comment
      } : r));
    } catch (e) {
      console.error("Erreur MAJ statut:", e?.response || e);
      alert(e?.response?.data?.error || "Échec de la mise à jour");
    }
  };

  /* ---------- Charger la liste EN ATTENTE (haut) ---------- */
  useEffect(() => {
    const loadPending = async () => {
      try {
        setLoading(true);
        const res = await api.get("/memoireValidation/district");
        const rows = (res.data || []).filter(m => (m.status ?? "en_attente") === "en_attente");
        setMemoires(rows);
        setErr("");
      } catch (e) {
        console.error("Erreur chargement mémoires (district) :", e);
        setErr("Impossible de charger les mémoires.");
      } finally {
        setLoading(false);
      }
    };
    loadPending();
  }, []);

  /* ---------- Helpers EN ATTENTE (haut) ---------- */
  const handleChangeComment = (id, value) => {
    setComments(prev => ({ ...prev, [id]: value }));
  };

  const upsertIntoAdminRowsFromPending = (m, apiStatus) => {
    // Vérifie le filtre Admin courant
    const statusMatches = !status || status === apiStatus;
    const regionMatches =
      !regionId ||
      String(regionId) === String(m.region_id ?? m.regionId ?? m.region ?? "");

    if (!statusMatches || !regionMatches) {
      return;
    }

    const newRow = {
      id: m.id,
      aspirant_name: m.aspirant_name ?? "—",
      region_name: m.region_name ?? "—",
      district_name: m.district_name ?? "—",
      eglise_name: m.eglise_name ?? "—",
      title: m.title ?? "—",
      file_path: m.file_path ?? null,
      status: apiStatus,
      comment: comments[m.id]?.trim() || m.comment || null,
      validated_at: apiStatus === "valide" ? new Date().toISOString() : m.validated_at ?? null,
    };

    setRows(prev => {
      const without = prev.filter(r => r.id !== m.id);
      return [newRow, ...without];
    });
  };

  const handleValidation = async (id, s) => {
    try {
      const apiStatus = s === 1 ? "valide" : "refuse";
      const comment = comments[id]?.trim() || null;

      if (apiStatus === "refuse" && !comment) {
        return alert("Merci d'ajouter un commentaire pour le refus.");
      }

      await api.patch(`/memoireValidation/${id}/validate`, { status: apiStatus, comment });

      const justValidated = memoires.find(x => x.id === id);

      setMemoires(prev => prev.filter(m => m.id !== id));

      setComments(prev => {
        const clone = { ...prev };
        delete clone[id];
        return clone;
      });

      if (justValidated) {
        upsertIntoAdminRowsFromPending(justValidated, apiStatus);
      }
    } catch (err) {
      console.error("Erreur lors de la mise à jour:", err?.response || err);
      alert("Erreur lors de la mise à jour");
    }
  };

  /* ---------- Utilitaires ---------- */
  const buildViewerUrl = (filePath) => {
    const raw = String(filePath || "");
    const b64 = window
      .btoa(unescape(encodeURIComponent(raw)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
    return `/admin/viewerAdmin?path=${encodeURIComponent(b64)}`;
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="fw-bold mb-0">Mémoires (Admin)</h4>
        <span className="badge bg-info text-dark">Total : {filtered.length}</span>
      </div>

      {/* ===== Tableau EN ATTENTE (haut) ===== */}
      <div className="card shadow border-0 mb-4">
        <div className="card-header">
          <h5 className="mb-0">Validation des mémoires</h5>
        </div>

        <div className="card-body">
          {loading && <p className="text-muted mb-0">Chargement…</p>}
          {err && <div className="alert alert-danger">{err}</div>}

          {!loading && !err && (
            <div
              className="table-responsive"
              style={{ overflowX: "auto", overflowY: "auto", maxHeight: "70vh", WebkitOverflowScrolling: "touch" }}
            >
              <table className="table table-hover align-middle mb-0 w-100" style={{ minWidth: 900 }}>
                <colgroup>
                  <col />                       
                  <col style={{ width: 140 }} /> 
                  <col />                      
                  <col style={{ width: 360 }} /> 
                  <col style={{ width: 160 }} /> 
                </colgroup>

                <thead className="table-light">
                  <tr>
                    <th className="text-nowrap">Élève</th>
                    <th className="text-nowrap">Mémoire</th>
                    <th className="text-nowrap d-none d-sm-table-cell">Titre</th>
                    <th className="text-nowrap">Commentaire</th>
                    <th className="text-end text-nowrap">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {memoires.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-4">
                        Aucun mémoire en attente.
                      </td>
                    </tr>
                  ) : (
                    memoires.map((m) => (
                      <tr key={m.id}>
                        {/* Élève */}
                        <td className="text-break">
                          <i className="bi bi-person me-2"></i>
                          {m.aspirant_name || "—"}
                        </td>

                        {/* Mémoire (btn lien) — FIX: utiliser m.file_path (et pas p.*) */}
                        <td className="text-nowrap">
                          {m.file_path ? (
                            <a
                              href={buildViewerUrl(m.file_path)}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-primary btn-sm text-nowrap"
                              title="Ouvrir dans la visionneuse"
                            >
                              Voir le doc
                            </a>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>

                        {/* Titre */}
                        <td className="text-break d-none d-sm-table-cell">
                          {m.title || "—"}
                        </td>

                        {/* Commentaire */}
                        <td style={{ minWidth: 260, maxWidth: 520 }}>
                          <label className="visually-hidden" htmlFor={`comment-${m.id}`}>
                            Commentaire
                          </label>
                          <textarea
                            id={`comment-${m.id}`}
                            className="form-control"
                            rows={2}
                            placeholder="Ex: Document incomplet, manque la signature…"
                            value={comments[m.id] || ""}
                            onChange={(e) => handleChangeComment(m.id, e.target.value)}
                            style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word" }}
                          />
                        </td>

                        {/* Actions */}
                        <td className="text-end">
                          <div className="btn-group btn-group-sm">
                            <button
                              onClick={() => handleValidation(m.id, 1)}
                              className="btn btn-success"
                            >
                              Valider
                            </button>
                            <button
                              onClick={() => handleValidation(m.id, 0)}
                              className="btn btn-danger"
                            >
                              Refuser
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ===== Filtres Admin (bas) ===== */}
      <div className="row g-2 mb-3">
        <div className="col-md-3">
          <select
            className="form-select"
            value={regionId}
            onChange={(e) => setRegionId(e.target.value)}
          >
            <option value="">Toutes les régions</option>
            {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        <div className="col-md-3">
          <select
            className="form-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">Tous statuts</option>
            <option value="en_attente">En attente</option>
            <option value="valide">Validés</option>
            <option value="refuse">Refusés</option>
          </select>
        </div>
        <div className="col-md-6">
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-search" /></span>
            <input
              type="text"
              className="form-control"
              placeholder="Recherche (nom, titre, église, …)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button className="btn btn-outline-secondary" onClick={load} disabled={loading}>
              Actualiser
            </button>
          </div>
        </div>
      </div>

      {/* ===== Tableau Admin (bas) ===== */}
      <div className="card shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="p-3 text-muted">Chargement…</div>
          ) : err ? (
            <div className="alert alert-danger m-3">{err}</div>
          ) : filtered.length === 0 ? (
            <div className="alert alert-info m-3">Aucun mémoire.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Élève</th>
                    <th>Région</th>
                    <th>District</th>
                    <th>Église</th>
                    <th>Titre</th>
                    <th>Fichier</th>
                    <th>Statut</th>
                    <th>Commentaire</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((m, idx) => (
                    <tr key={m.id}>
                      <td>{idx + 1}</td>
                      <td>{m.aspirant_name || "—"}</td>
                      <td>{m.region_name || "—"}</td>
                      <td>{m.district_name || "—"}</td>
                      <td>{m.eglise_name || "—"}</td>
                      <td>{m.title || "—"}</td>
                      <td className="text-nowrap">
                          {m.file_path ? (
                            <a
                              href={buildViewerUrl(m.file_path)}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-primary btn-sm text-nowrap"
                              title="Ouvrir dans la visionneuse"
                            >
                              Voir le doc
                            </a>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                      </td>
                      <td>
                        <span className={`badge bg-${badge(m.status)}`}>
                          {m.status || "en_attente"}
                        </span>
                      </td>
                      <td style={{ minWidth: 220 }}>
                        <textarea
                          className="form-control form-control-sm"
                          rows="1"
                          placeholder="Ajouter/éditer un commentaire…"
                          value={comments[m.id] ?? m.comment ?? ""}
                          onChange={(e) => onChangeComment(m.id, e.target.value)}
                        />
                      </td>
                      <td className="text-end">
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-outline-info"
                            onClick={() => openCommentModal(m)}
                          >
                            <i className="bi bi-chat-dots" />
                          </button>
                          {m.status === "en_attente" && (
                            <>
                              <button
                                className="btn btn-sm btn-outline-success"
                                onClick={() => updateStatus(m.id, "valide")}
                              >
                                Valider
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => updateStatus(m.id, "refuse")}
                              >
                                Refuser
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ===== Modale commentaires (Admin bas) ===== */}
      {showCommentModal && selectedMemoire && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
          onClick={closeCommentModal}
        >
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Commentaire — {selectedMemoire.aspirant_name}
                </h5>
                <button type="button" className="btn-close" onClick={closeCommentModal} />
              </div>
              <div className="modal-body">
                <p className="mb-2"><strong>Titre :</strong> {selectedMemoire.title || "—"}</p>
                <textarea
                  value={comments[selectedMemoire.id] || ""}
                  onChange={(e) => onChangeComment(selectedMemoire.id, e.target.value)}
                  className="form-control"
                  rows="5"
                  placeholder="Votre commentaire…"
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeCommentModal}>Fermer</button>
                <button className="btn btn-success" onClick={() => { updateStatus(selectedMemoire.id, "valide"); closeCommentModal(); }}>
                  Valider
                </button>
                <button className="btn btn-danger" onClick={() => { updateStatus(selectedMemoire.id, "refuse"); closeCommentModal(); }}>
                  Refuser
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
